import type { ColumnDef } from '@tanstack/react-table'
import { Badge } from '@/components/ui/badge'
import { DataTableColumnHeader } from '@/components/data-table'
import { formatDateTime } from '@/lib/format'

export type AdminAuditRow = {
  id: string
  actor_id: string
  actor_username: string | null
  actor_display_name: string | null
  action: string
  target_type: string | null
  target_id: string | null
  meta: Record<string, unknown> | null
  created_at: string
}

export const ADMIN_AUDIT_SORT_IDS = ['createdAt'] as const

export const ADMIN_AUDIT_FILTER_KEYS = [
  'id',
  'action',
  'target_type',
  'target_id',
] as const

export function formatAuditActor(item: {
  actor_username: string | null
  actor_display_name: string | null
  actor_id: string
}): string {
  return (
    item.actor_username || item.actor_display_name || item.actor_id.slice(0, 8)
  )
}

export function formatAuditTarget(
  type: string | null,
  id: string | null,
): string {
  if (!type && !id) return '—'
  if (type && id)
    return `${type}:${id.length > 12 ? `${id.slice(0, 12)}…` : id}`
  return type ?? id ?? '—'
}

export function formatAuditMeta(meta: Record<string, unknown> | null): string {
  if (!meta || Object.keys(meta).length === 0) return '—'
  try {
    const s = JSON.stringify(meta)
    return s.length > 80 ? `${s.slice(0, 80)}…` : s
  } catch {
    return '—'
  }
}

export function createAdminAuditColumns(): ColumnDef<AdminAuditRow, unknown>[] {
  return [
    {
      id: 'createdAt',
      accessorKey: 'created_at',
      meta: { label: 'When' },
      enableSorting: false,
      enableHiding: true,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="When" />
      ),
      cell: ({ row }) => (
        <span className="whitespace-nowrap font-mono text-[11px] text-muted-foreground">
          {formatDateTime(row.original.created_at)}
        </span>
      ),
    },
    {
      id: 'actor',
      accessorFn: (r) => formatAuditActor(r),
      meta: { label: 'Actor' },
      enableSorting: false,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Actor" />
      ),
      cell: ({ row }) => (
        <div className="min-w-0">
          <span className="text-sm font-medium">
            {formatAuditActor(row.original)}
          </span>
          <span className="mt-0.5 block font-mono text-[10px] text-muted-foreground">
            {row.original.actor_id}
          </span>
        </div>
      ),
    },
    {
      id: 'action',
      accessorKey: 'action',
      meta: {
        label: 'Action',
        variant: 'text',
        placeholder: 'users.set_role',
      },
      enableSorting: false,
      enableColumnFilter: true,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Action" />
      ),
      cell: ({ row }) => (
        <Badge variant="secondary" className="font-mono text-[11px]">
          {row.original.action}
        </Badge>
      ),
    },
    {
      id: 'target_type',
      accessorKey: 'target_type',
      meta: {
        label: 'Target type',
        variant: 'text',
        placeholder: 'user, report…',
      },
      enableSorting: false,
      enableColumnFilter: true,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Target" />
      ),
      cell: ({ row }) => (
        <span className="font-mono text-[11px] text-muted-foreground">
          {formatAuditTarget(row.original.target_type, row.original.target_id)}
        </span>
      ),
    },
    {
      id: 'target_id',
      accessorKey: 'target_id',
      meta: {
        label: 'Target id',
        variant: 'text',
        placeholder: 'Exact target id',
      },
      enableSorting: false,
      enableColumnFilter: true,
      enableHiding: true,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Target id" />
      ),
      cell: ({ row }) => (
        <span className="font-mono text-[11px] text-muted-foreground">
          {row.original.target_id ?? '—'}
        </span>
      ),
    },
    {
      id: 'id',
      accessorKey: 'id',
      meta: {
        label: 'Audit id',
        variant: 'text',
        placeholder: 'Exact audit id',
      },
      enableSorting: false,
      enableColumnFilter: true,
      enableHiding: true,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Id" />
      ),
      cell: ({ row }) => (
        <span className="font-mono text-[11px] text-muted-foreground">
          {row.original.id}
        </span>
      ),
    },
    {
      id: 'meta',
      accessorFn: (r) => formatAuditMeta(r.meta),
      meta: { label: 'Meta' },
      enableSorting: false,
      enableHiding: true,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Meta" />
      ),
      cell: ({ row }) => (
        <span
          className="block max-w-xs truncate font-mono text-[11px] text-muted-foreground"
          title={
            row.original.meta ? JSON.stringify(row.original.meta) : undefined
          }
        >
          {formatAuditMeta(row.original.meta)}
        </span>
      ),
    },
  ]
}
